import { defineComponent as _defineComponent } from 'vue'
import { biClick } from '@tencent/fit-bi-sdk';
import { computed, nextTick, onMounted, ref, useAttrs, watch } from 'vue-demi';
import ScanGift from '../scan-gift/scan-gift.vue';
import Accordion from './components/accordion/accordion.vue';
import C2CMain from './components/c2c-main/index.vue';
import { useAccordion } from './hooks/use-accordion';
import { useActInfo } from './hooks/use-act-info';
import { useC2cScanGift } from './hooks/use-c2c-scan-gift';
import { useHighLogin } from './hooks/use-high-login';
import { i18n } from './i18n';
import { C2CShareType, C2CToastType } from './types';


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  emits: ['onHideC2C', 'showState', 'isReady', 'onRestWxShare'],
  setup(__props, { emit: emits }) {


const attrs = useAttrs();

const c2cMain = ref<InstanceType<typeof C2CMain> | null>(null);

const {
  scanGift,
  scanGiftExt,
  isScanGiftShow,
  isScanGiftInited,
  isAllScanGiftCouponReceived,
  isAllScanGiftCouponNotReceived,
  receiveScanGiftCoupon,
  onShowState,
  onCompReady,
} = useC2cScanGift();

const { accordionRef, processAccordion } = useAccordion();

const { highAuthLoginBackData, setHighAuthLoginBackData, highAuthLogin, checkHighAndLogin } = useHighLogin();

const { isSelfUrlShareCode, shareCodeUserInfo } = useActInfo();

const isShowC2C = ref(false);
const isInitedWithCode = ref(false);

const isShowNickName = ref(false);
const titlePrefix = computed(() => {
  const nickName = isShowNickName.value ? shareCodeUserInfo.value?.nickName : '';
  return nickName ? `${nickName}${i18n('送你')}` : '';
});

const disSource = ref('');
const disSourceTmp = ref('');

/** 扫码有礼组件 - 是否展示事件 */
function handleShowState(isShow: boolean) {
  emits('showState', isShow);
  onShowState(isShow);
}

/** 扫码有礼组件 - 加载完成事件 */
function handleCompReady(isReady: boolean, data) {
  emits('isReady', isReady, data);
  processAccordion(data.titleForFixed, data.fixedCouponDetailList, attrs.isSupportAccordion);
  onCompReady(data);
  // 高授权处理（需要扫码有礼组件初始化完成后）
  if (highAuthLoginBackData.value) {
    const {
      shareType,
      coupon,
      disSource: disSourceVal,
      isShowNickName: isShowNickNameVal,
    } = highAuthLoginBackData.value;
    if (shareType === C2CShareType.C2CInvitee) {
      disSource.value = disSourceVal; // 只有非本人&&没领过券才传分享码
      isShowNickName.value = isShowNickNameVal;
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      nextTick(async () => {
        setHighAuthLoginBackData(null);
        if (!coupon) return;
        await receiveScanGiftCoupon(coupon);
      });
    }
  }
}

/** 扫码有礼组件 - 领券前 */
async function preCheckReceiveCoupon(coupon) {
  biClick('c2c.scangift_click', scanGiftExt);
  await checkHighAndLogin({
    shareType: C2CShareType.C2CInvitee,
    coupon,
    disSource: disSource.value,
    isShowNickName: isShowNickName.value,
  });
}

/** C2C组件展示 */
function onShowC2C() {
  isShowC2C.value = true;
}

/** C2C组件隐藏 */
function onHideC2C() {
  emits('onHideC2C');
}

/** C2C组件 - 带分享码场景时初始化完成 */
function onInitedWithCode({ disSource, hasShowInviteChangeDialog }) {
  // 如果进入本人分享链接，展示过邀请变更弹窗，和「邀请自己无法获得奖赏」互斥
  if (isSelfUrlShareCode.value && hasShowInviteChangeDialog) return;

  isInitedWithCode.value = true;
  disSourceTmp.value = disSource;
}

/** C2C组件(带分享码场景时)和扫码有礼组件都初始化完成 - 本人 */
function handleSelfInited() {
  // 目标用户（能展示扫码有礼组件）&& 没有领过券
  if (isScanGiftShow.value && isAllScanGiftCouponNotReceived.value) {
    c2cMain.value?.showC2CToast(C2CToastType.C2CInviteeSelf);
  }
}

/** C2C组件(带分享码场景时)和扫码有礼组件都初始化完成 - 非本人 */
function handleNotSelfInited() {
  // 非目标用户（不能展示扫码有礼组件）
  if (!isScanGiftShow.value) {
    c2cMain.value?.showNonTargetUserDialog();
    return;
  }
  // 1. 领过券
  if (!isAllScanGiftCouponNotReceived.value) {
    // 非本人 && 领过券 && 绑定过关系 => 展示昵称（绑定过的人昵称)
    if (shareCodeUserInfo.value?.hasHelp) {
      isShowNickName.value = true;
    }
    // 1.1 全部领取
    if (isAllScanGiftCouponReceived.value) {
      c2cMain.value?.showC2CToast(C2CToastType.C2CInviteeReceivedAll);
      return;
    }
    // 1.2 部分领取
    return;
  }
  // 2. 没领过券
  // 非本人 && 没领过券 => 展示昵称（绑定过的人昵称>当前分享人昵称）
  isShowNickName.value = true;
  // 非本人 && 没领过券 && 没有绑定过关系 => 传分享码
  if (!shareCodeUserInfo.value?.hasHelp) {
    disSource.value = disSourceTmp.value;
  }
  scanGift.value?.showPromptDialog();
}

/** 分享重置 */
function onRestWxShare() {
  emits('onRestWxShare');
}

watch(
  () => [isInitedWithCode.value, isScanGiftInited.value],
  (newVal, oldVal) => {
    const [newIsInitedWithCode, newIsScanGiftInited] = newVal;
    // C2C组件(带分享码场景时)和扫码有礼组件都初始化完成
    if (newIsInitedWithCode && newIsScanGiftInited) {
      isSelfUrlShareCode.value ? handleSelfInited() : handleNotSelfInited();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  let isHighAuthLoginBack = false;
  highAuthLogin.afterHighAuthLogin(async (data) => {
    isHighAuthLoginBack = true;
    setHighAuthLoginBackData(data);
    await c2cMain.value?.init(true);
    if (data.shareType === C2CShareType.C2CSharer) {
      setHighAuthLoginBackData(null);
      // 本人分享，拉起分享弹窗，并拉起分享组件
      c2cMain.value?.afterHighAuthLoginShare(data.sharerAction);
    }
  });
  if (!isHighAuthLoginBack) {
    await c2cMain.value?.init();
  }
});

return { __sfc: true,emits, attrs, c2cMain, scanGift, scanGiftExt, isScanGiftShow, isScanGiftInited, isAllScanGiftCouponReceived, isAllScanGiftCouponNotReceived, receiveScanGiftCoupon, onShowState, onCompReady, accordionRef, processAccordion, highAuthLoginBackData, setHighAuthLoginBackData, highAuthLogin, checkHighAndLogin, isSelfUrlShareCode, shareCodeUserInfo, isShowC2C, isInitedWithCode, isShowNickName, titlePrefix, disSource, disSourceTmp, handleShowState, handleCompReady, preCheckReceiveCoupon, onShowC2C, onHideC2C, onInitedWithCode, handleSelfInited, handleNotSelfInited, onRestWxShare, ScanGift, Accordion, C2CMain, i18n }
}

})